import * as THREE from 'three';

import {
  type LabSceneFactory,
  type LabStage,
  createLabStage,
  labGlassEdgeMaterial,
  labGlassMaterial,
  refractiveLiquidMaterial,
  seeded,
} from './labScene3d';
import type { SurfaceArea } from './reactionRates';

/**
 * Shared 3D gas-syringe apparatus for the Gas Laws Lab and the Reaction Rate lab. Every value comes
 * from the deterministic Explore model; the scene is a teaching view and is not to scale.
 * Only the acid is refractive; glass, gas, bubbles and marble use transparent or opaque materials.
 */
export interface GasLawsSyringeState {
  volumeL: number;
  /** Volume drawn as a full syringe (the Explore volume limit). */
  capacityL: number;
  pressureKPa: number;
  temperatureK: number;
}

export interface ReactionGasState {
  acidVolumeMl: number;
  surfaceArea: SurfaceArea;
  /** Gas collected at the chosen time, already stopped at the syringe capacity. */
  gasMl: number;
  capacityMl: number;
  /** 0–1: how fast bubbles form now, from the model's current rate. */
  bubbleRate: number;
  /** 0–1: share of the acid used up; the marble shrinks a little. */
  progress: number;
}

const GAUGE_MAX_KPA = 1000;
const T_MIN_K = 150;
const T_MAX_K = 1000;
const FLASK_CAPACITY_ML = 100;

export const GAS_LAWS_PREVIEW_STATES: Record<string, GasLawsSyringeState> = {
  start: { volumeL: 10, capacityL: 25, pressureKPa: 100, temperatureK: 298.15 },
  compressed: { volumeL: 5, capacityL: 25, pressureKPa: 400, temperatureK: 298.15 },
  expanded: { volumeL: 20, capacityL: 25, pressureKPa: 50, temperatureK: 298.15 },
  heated: { volumeL: 12.73, capacityL: 25, pressureKPa: 100, temperatureK: 373.15 },
  'pressure-limit': { volumeL: 1, capacityL: 25, pressureKPa: 1000, temperatureK: 298.15 },
  cold: { volumeL: 5.1, capacityL: 25, pressureKPa: 100, temperatureK: 150 },
};

export const REACTION_RATE_PREVIEW_STATES: Record<string, ReactionGasState> = {
  start: { acidVolumeMl: 5, surfaceArea: 'lump', gasMl: 0, capacityMl: 100, bubbleRate: 0.25, progress: 0 },
  'lump-half': { acidVolumeMl: 5, surfaceArea: 'lump', gasMl: 31, capacityMl: 100, bubbleRate: 0.18, progress: 0.5 },
  chips: { acidVolumeMl: 20, surfaceArea: 'chips', gasMl: 60, capacityMl: 100, bubbleRate: 0.45, progress: 0.3 },
  'powder-fast': { acidVolumeMl: 5, surfaceArea: 'powder', gasMl: 55, capacityMl: 100, bubbleRate: 0.7, progress: 0.88 },
  'syringe-full': { acidVolumeMl: 50, surfaceArea: 'chips', gasMl: 100, capacityMl: 100, bubbleRate: 0.9, progress: 0.2 },
};

interface GasSyringe {
  group: THREE.Group;
  /** World position of the nozzle opening. */
  tip: THREE.Vector3;
  radius: number;
  setFill: (fraction: number) => void;
  setGasOpacity: (opacity: number) => void;
}

/** A horizontal glass gas syringe with graduations; the nozzle points to −x from `tip`. */
function buildGasSyringe(stage: LabStage, tip: THREE.Vector3, options: { length?: number; radius?: number; ticks?: number } = {}): GasSyringe {
  const { palette, keep, benchY } = stage;
  const L = options.length ?? 3.6;
  const R = options.radius ?? 0.5;
  const nozzle = 0.45;
  const x0 = nozzle;
  const group = new THREE.Group();
  group.position.copy(tip);

  const barrelMaterial = labGlassMaterial(stage, 0.2);
  barrelMaterial.side = THREE.DoubleSide;
  const barrel = new THREE.Mesh(keep(new THREE.CylinderGeometry(R, R, L, 64, 1, true)), barrelMaterial);
  barrel.rotation.z = -Math.PI / 2;
  barrel.position.x = x0 + L / 2;
  barrel.renderOrder = 10;
  const edge = labGlassEdgeMaterial(stage);
  const ring = keep(new THREE.TorusGeometry(R, 0.035, 10, 64));
  [x0, x0 + L].forEach((x) => {
    const rim = new THREE.Mesh(ring, edge);
    rim.rotation.y = Math.PI / 2;
    rim.position.x = x;
    rim.renderOrder = 11;
    group.add(rim);
  });
  const flange = new THREE.Mesh(keep(new THREE.TorusGeometry(R + 0.12, 0.05, 10, 64)), edge);
  flange.rotation.y = Math.PI / 2;
  flange.position.x = x0 + L;
  const end = new THREE.Mesh(keep(new THREE.CircleGeometry(R, 48)), labGlassMaterial(stage, 0.3));
  end.rotation.y = -Math.PI / 2;
  end.position.x = x0;
  const nozzleMesh = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.1, 0.16, nozzle, 24)), edge);
  nozzleMesh.rotation.z = -Math.PI / 2;
  nozzleMesh.position.x = nozzle / 2;
  group.add(barrel, flange, end, nozzleMesh);

  // Graduation marks on the front-top of the barrel.
  const ticks = options.ticks ?? 10;
  const markMaterial = keep(new THREE.MeshBasicMaterial({ color: palette.metal }));
  const markAngle = 0.55;
  for (let index = 1; index <= ticks; index += 1) {
    const major = index % 5 === 0;
    const mark = new THREE.Mesh(keep(new THREE.BoxGeometry(0.018, 0.012, major ? 0.32 : 0.18)), markMaterial);
    mark.position.set(x0 + 0.08 + (index / ticks) * (L - 0.3), Math.cos(markAngle) * (R + 0.004), Math.sin(markAngle) * (R + 0.004));
    mark.rotation.x = markAngle;
    group.add(mark);
  }

  // Faint gas between the nozzle end and the plunger.
  const gasMaterial = keep(new THREE.MeshPhysicalMaterial({
    color: palette.steam.clone().lerp(palette.liquid, 0.35), roughness: 0.3, transparent: true, opacity: 0.1, depthWrite: false,
  }));
  const gas = new THREE.Mesh(keep(new THREE.CylinderGeometry(R - 0.03, R - 0.03, 1, 48)), gasMaterial);
  gas.rotation.z = -Math.PI / 2;
  gas.renderOrder = 9;
  group.add(gas);

  // Plunger: piston, rod and thumb plate, all opaque.
  const plunger = new THREE.Group();
  const pistonMaterial = keep(new THREE.MeshPhysicalMaterial({ color: palette.metal.clone().lerp(palette.bench, 0.35), roughness: 0.55 }));
  const plasticMaterial = keep(new THREE.MeshPhysicalMaterial({ color: palette.bench, roughness: 0.35, clearcoat: 0.4 }));
  const piston = new THREE.Mesh(keep(new THREE.CylinderGeometry(R - 0.015, R - 0.015, 0.22, 48)), pistonMaterial);
  piston.rotation.z = -Math.PI / 2;
  piston.position.x = 0.11;
  const rodLength = L + 0.3;
  const rod = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.11, 0.11, rodLength, 20)), plasticMaterial);
  rod.rotation.z = -Math.PI / 2;
  rod.position.x = 0.22 + rodLength / 2;
  const thumb = new THREE.Mesh(keep(new THREE.CylinderGeometry(R * 0.85, R * 0.85, 0.08, 40)), plasticMaterial);
  thumb.rotation.z = -Math.PI / 2;
  thumb.position.x = 0.22 + rodLength;
  [piston, rod, thumb].forEach((mesh) => {
    mesh.castShadow = true;
    plunger.add(mesh);
  });
  group.add(plunger);

  // Two support blocks from the bench to the barrel.
  const blockHeight = tip.y - R - benchY;
  if (blockHeight > 0.05) {
    const blockMaterial = keep(new THREE.MeshPhysicalMaterial({ color: palette.metal, metalness: 0.5, roughness: 0.4 }));
    const blockGeometry = keep(new THREE.BoxGeometry(0.45, blockHeight, 0.9));
    [x0 + 0.5, x0 + L - 0.5].forEach((x) => {
      const block = new THREE.Mesh(blockGeometry, blockMaterial);
      block.position.set(x, -R - blockHeight / 2, 0);
      block.castShadow = true;
      block.receiveShadow = true;
      group.add(block);
    });
  }
  stage.scene.add(group);

  const travel = L - 0.3;
  return {
    group,
    tip: tip.clone(),
    radius: R,
    setFill: (fraction) => {
      const f = THREE.MathUtils.clamp(fraction, 0, 1);
      const face = x0 + 0.02 + f * travel;
      plunger.position.x = face;
      const length = Math.max(face - x0, 0.001);
      gas.scale.y = length;
      gas.position.x = x0 + length / 2;
      gas.visible = f > 0.002;
    },
    setGasOpacity: (opacity) => {
      gasMaterial.opacity = opacity;
    },
  };
}

/** Resize handler that pulls the camera back on narrow screens so the whole apparatus fits. */
function fitToWidth(stage: LabStage, halfWidth: number) {
  const minDistance = stage.camera.position.distanceTo(stage.controls.target);
  return (width: number, height: number) => {
    stage.resize(width, height);
    const halfFov = THREE.MathUtils.degToRad(stage.camera.fov / 2);
    const distance = Math.max(minDistance, halfWidth / (Math.tan(halfFov) * (width / height)));
    const offset = stage.camera.position.clone().sub(stage.controls.target).setLength(distance);
    stage.camera.position.copy(stage.controls.target).add(offset);
    stage.controls.update();
  };
}

/** A vertical thermometer whose red column follows a 0–1 reading. */
function buildThermometer(stage: LabStage, bottom: THREE.Vector3, length: number) {
  const { palette, keep } = stage;
  const group = new THREE.Group();
  group.position.copy(bottom);
  const tubeMaterial = labGlassMaterial(stage, 0.35);
  tubeMaterial.side = THREE.DoubleSide;
  const tube = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.09, 0.09, length, 20)), tubeMaterial);
  tube.position.y = length / 2;
  tube.renderOrder = 12;
  const liquid = keep(new THREE.MeshPhysicalMaterial({ color: palette.accent, roughness: 0.2, clearcoat: 1, emissive: palette.accent, emissiveIntensity: 0.35 }));
  const bulb = new THREE.Mesh(keep(new THREE.SphereGeometry(0.14, 20, 14)), liquid);
  const column = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.045, 0.045, 1, 12)), liquid);
  group.add(tube, bulb, column);
  stage.scene.add(group);
  return {
    setReading: (reading: number) => {
      const columnLength = 0.12 + THREE.MathUtils.clamp(reading, 0, 1) * (length - 0.4);
      column.scale.y = columnLength;
      column.position.y = columnLength / 2;
    },
  };
}

/** Pressure gauge facing the camera, joined to the syringe nozzle by a short metal pipe. */
function buildGauge(stage: LabStage, centre: THREE.Vector3, pipeBottomY: number, radius = 0.68) {
  const { palette, keep } = stage;
  const metal = keep(new THREE.MeshPhysicalMaterial({ color: palette.metal, metalness: 0.8, roughness: 0.3, clearcoat: 0.6 }));
  const body = new THREE.Mesh(keep(new THREE.CylinderGeometry(radius, radius, 0.2, 64)), metal);
  body.rotation.x = Math.PI / 2;
  body.position.copy(centre);
  body.castShadow = true;
  const face = new THREE.Mesh(keep(new THREE.CircleGeometry(radius - 0.07, 64)), keep(new THREE.MeshStandardMaterial({ color: palette.bench, roughness: 0.5 })));
  face.position.set(centre.x, centre.y, centre.z + 0.101);
  const pipeLength = centre.y - radius - pipeBottomY;
  const pipe = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.06, 0.06, pipeLength, 16)), metal);
  pipe.position.set(centre.x, pipeBottomY + pipeLength / 2, centre.z);
  stage.scene.add(body, face, pipe);

  const start = Math.PI * 0.75;
  const sweep = Math.PI * 1.5;
  const tickMaterial = keep(new THREE.MeshBasicMaterial({ color: palette.metal }));
  for (let index = 0; index <= 10; index += 1) {
    const angle = start - (index / 10) * sweep;
    const major = index % 5 === 0;
    const tick = new THREE.Mesh(keep(new THREE.BoxGeometry(0.025, major ? 0.13 : 0.08, 0.01)), tickMaterial);
    const r = radius - 0.16;
    tick.position.set(centre.x - Math.sin(angle) * r, centre.y + Math.cos(angle) * r, centre.z + 0.105);
    tick.rotation.z = angle;
    stage.scene.add(tick);
  }
  const needle = new THREE.Group();
  needle.position.set(centre.x, centre.y, centre.z + 0.115);
  const needleMaterial = keep(new THREE.MeshStandardMaterial({ color: palette.accent, roughness: 0.4 }));
  const pointer = new THREE.Mesh(keep(new THREE.BoxGeometry(0.035, radius - 0.18, 0.012)), needleMaterial);
  pointer.position.y = (radius - 0.18) / 2;
  const hub = new THREE.Mesh(keep(new THREE.SphereGeometry(0.05, 16, 12)), metal);
  needle.add(pointer, hub);
  stage.scene.add(needle);
  return {
    setReading: (reading: number) => {
      needle.rotation.z = start - THREE.MathUtils.clamp(reading, 0, 1) * sweep;
    },
  };
}

/** Gas Laws Lab: sealed gas syringe, pressure gauge and thermometer. */
export const createGasLawsSyringeScene: LabSceneFactory<GasLawsSyringeState> = (renderer, palette, reducedMotion) => {
  const stage = createLabStage(renderer, palette, reducedMotion, { cameraPosition: [-0.6, 2.6, 12.4], target: [-0.9, 0.05, 0] });
  const syringeY = -0.75;
  const syringe = buildGasSyringe(stage, new THREE.Vector3(-3.0, syringeY, 0), { length: 3.6, radius: 0.55 });
  const gauge = buildGauge(stage, new THREE.Vector3(-3.0, 0.75, 0), syringeY);
  const thermometer = buildThermometer(stage, new THREE.Vector3(0.9, syringeY - 0.25, -0.75), 2.9);

  // Retort stand holding the thermometer.
  const standMaterial = stage.keep(new THREE.MeshPhysicalMaterial({ color: palette.metal, metalness: 0.6, roughness: 0.35 }));
  const base = new THREE.Mesh(stage.keep(new THREE.BoxGeometry(1.1, 0.1, 0.7)), standMaterial);
  base.position.set(0.9, stage.benchY + 0.05, -1.55);
  const rodHeight = 4.4;
  const rod = new THREE.Mesh(stage.keep(new THREE.CylinderGeometry(0.05, 0.05, rodHeight, 16)), standMaterial);
  rod.position.set(0.9, stage.benchY + rodHeight / 2, -1.55);
  const arm = new THREE.Mesh(stage.keep(new THREE.BoxGeometry(0.07, 0.07, 0.8)), standMaterial);
  arm.position.set(0.9, 1.6, -1.15);
  [base, rod, arm].forEach((mesh) => {
    mesh.castShadow = true;
    stage.scene.add(mesh);
  });
  stage.finish();

  return {
    update: (state, _t, playing) => {
      const fill = state.capacityL > 0 ? state.volumeL / state.capacityL : 0;
      syringe.setFill(Math.max(fill, 0.02));
      // Denser gas (higher p at the same T) looks a little more tinted.
      syringe.setGasOpacity(0.06 + 0.2 * THREE.MathUtils.clamp(state.pressureKPa / GAUGE_MAX_KPA, 0, 1));
      gauge.setReading(state.pressureKPa / GAUGE_MAX_KPA);
      thermometer.setReading((state.temperatureK - T_MIN_K) / (T_MAX_K - T_MIN_K));
      stage.render(playing);
    },
    resize: fitToWidth(stage, 3.5),
    dispose: stage.dispose,
  };
};

const FLASK = { base: 1.05, neck: 0.3, coneTop: 1.85, top: 2.7, wall: 0.05 } as const;

function flaskInnerRadius(y: number): number {
  if (y <= 0.14) return FLASK.base - FLASK.wall - 0.02;
  if (y >= FLASK.coneTop) return FLASK.neck - FLASK.wall;
  const share = (y - 0.14) / (FLASK.coneTop - 0.14);
  return FLASK.base - FLASK.wall - share * (FLASK.base - FLASK.neck);
}

function liquidVolume(height: number): number {
  const steps = 40;
  let volume = 0;
  for (let index = 0; index < steps; index += 1) {
    const y = 0.05 + ((index + 0.5) / steps) * (height - 0.05);
    volume += Math.PI * flaskInnerRadius(y) ** 2 * ((height - 0.05) / steps);
  }
  return volume;
}

/** Liquid height for a share of the flask volume up to the neck, by bisection. */
function liquidHeight(share: number): number {
  const total = liquidVolume(FLASK.coneTop);
  let low: number = 0.06;
  let high: number = FLASK.coneTop;
  for (let index = 0; index < 30; index += 1) {
    const mid = (low + high) / 2;
    if (liquidVolume(mid) < share * total) low = mid;
    else high = mid;
  }
  return (low + high) / 2;
}

function liquidProfile(height: number): THREE.Vector2[] {
  const points = [new THREE.Vector2(0, 0.05), new THREE.Vector2(flaskInnerRadius(0.06), 0.05)];
  const steps = 12;
  for (let index = 1; index <= steps; index += 1) {
    const y = 0.05 + (index / steps) * (height - 0.05);
    points.push(new THREE.Vector2(flaskInnerRadius(y) - 0.004, y));
  }
  points.push(new THREE.Vector2(0, height));
  return points;
}

const CHIP_COUNTS: Record<SurfaceArea, { count: number; size: number; spread: number }> = {
  lump: { count: 1, size: 0.34, spread: 0 },
  chips: { count: 8, size: 0.15, spread: 0.55 },
  powder: { count: 90, size: 0.045, spread: 0.75 },
};
const MAX_CHIPS = 90;
const BUBBLES = 60;

/** Reaction Rate lab: conical flask of acid with marble, delivery tube and gas syringe. */
export const createReactionGasScene: LabSceneFactory<ReactionGasState> = (renderer, palette, reducedMotion) => {
  const stage = createLabStage(renderer, palette, reducedMotion, { cameraPosition: [-0.2, 2.8, 13.2], target: [-0.4, -0.2, 0] });
  const { scene, keep, benchY } = stage;
  const flaskX = -2.6;
  const floorY = benchY;

  // Conical flask from a lathe profile with wall thickness, rim and base edge.
  const outer = [
    [0, 0], [FLASK.base - 0.1, 0], [FLASK.base - 0.02, 0.03], [FLASK.base, 0.12], [FLASK.neck, FLASK.coneTop],
    [FLASK.neck, FLASK.top - 0.08], [FLASK.neck + 0.05, FLASK.top - 0.05], [FLASK.neck + 0.05, FLASK.top],
    [FLASK.neck - FLASK.wall, FLASK.top], [FLASK.neck - FLASK.wall, FLASK.coneTop], [FLASK.base - FLASK.wall, 0.14],
    [FLASK.base - FLASK.wall - 0.03, FLASK.wall], [0, FLASK.wall],
  ].map(([x, y]) => new THREE.Vector2(x, y));
  const flask = new THREE.Mesh(keep(new THREE.LatheGeometry(outer, 96)), labGlassMaterial(stage, 0.16));
  flask.position.set(flaskX, floorY, 0);
  flask.renderOrder = 10;
  const edge = labGlassEdgeMaterial(stage);
  const rim = new THREE.Mesh(keep(new THREE.TorusGeometry(FLASK.neck + 0.03, 0.035, 10, 48)), edge);
  rim.rotation.x = Math.PI / 2;
  rim.position.set(flaskX, floorY + FLASK.top, 0);
  const baseRing = new THREE.Mesh(keep(new THREE.TorusGeometry(FLASK.base - 0.04, 0.04, 10, 96)), edge);
  baseRing.rotation.x = Math.PI / 2;
  baseRing.position.set(flaskX, floorY + 0.06, 0);
  scene.add(flask, rim, baseRing);

  // Acid: refractive, pale (dilute hydrochloric acid is colourless).
  const acidMaterial = refractiveLiquidMaterial(stage, palette.liquid, { tint: 0.75, attenuationDistance: 9 });
  let acidGeometry = new THREE.LatheGeometry(liquidProfile(0.3), 72);
  const acid = new THREE.Mesh(acidGeometry, acidMaterial);
  acid.position.set(flaskX, floorY, 0);
  scene.add(acid);
  let acidHeight = 0;
  let lastAcidMl = -1;

  // Marble: opaque, irregular faceted lumps.
  const lumpGeometry = keep(new THREE.IcosahedronGeometry(1, 1));
  const position = lumpGeometry.getAttribute('position') as THREE.BufferAttribute;
  for (let index = 0; index < position.count; index += 1) {
    const jitter = 0.78 + seeded(Math.round((position.getX(index) + position.getY(index) * 3 + position.getZ(index) * 7) * 100), 3) * 0.4;
    position.setXYZ(index, position.getX(index) * jitter, position.getY(index) * jitter * 0.8, position.getZ(index) * jitter);
  }
  lumpGeometry.computeVertexNormals();
  const marble = new THREE.InstancedMesh(
    lumpGeometry,
    keep(new THREE.MeshPhysicalMaterial({ color: palette.bench.clone().lerp(palette.metal, 0.14), roughness: 0.75, flatShading: true })),
    MAX_CHIPS,
  );
  marble.castShadow = true;
  scene.add(marble);
  const chipSpots = Array.from({ length: MAX_CHIPS }, (_, index) => ({
    angle: seeded(index, 21) * Math.PI * 2,
    radius: Math.sqrt(seeded(index, 22)),
    spin: seeded(index, 23) * Math.PI * 2,
    tilt: seeded(index, 24) * 0.6,
    scale: 0.8 + seeded(index, 25) * 0.4,
  }));

  // Bubbles: transparent, never refractive.
  const bubbles = new THREE.InstancedMesh(
    keep(new THREE.SphereGeometry(0.045, 12, 8)),
    keep(new THREE.MeshPhysicalMaterial({ color: palette.steam, roughness: 0.05, transparent: true, opacity: 0.75, clearcoat: 1, envMapIntensity: 2.5 })),
    BUBBLES,
  );
  scene.add(bubbles);

  // Rubber bung and glass delivery tube to the syringe nozzle.
  const bung = new THREE.Mesh(
    keep(new THREE.CylinderGeometry(FLASK.neck + 0.04, FLASK.neck - 0.03, 0.4, 32)),
    keep(new THREE.MeshPhysicalMaterial({ color: palette.metal.clone().lerp(palette.heat, 0.35), roughness: 0.8 })),
  );
  bung.position.set(flaskX, floorY + FLASK.top + 0.05, 0);
  bung.castShadow = true;
  scene.add(bung);

  const syringeY = benchY + 0.95;
  const syringe = buildGasSyringe(stage, new THREE.Vector3(-0.6, syringeY, 0), { length: 3.2, radius: 0.5 });
  const tubeTop = floorY + FLASK.top + 1.0;
  const curve = new THREE.CatmullRomCurve3([
    new THREE.Vector3(flaskX, floorY + FLASK.top - 0.3, 0),
    new THREE.Vector3(flaskX, tubeTop - 0.2, 0),
    new THREE.Vector3(flaskX + 0.35, tubeTop, 0),
    new THREE.Vector3(-1.5, tubeTop - 0.2, 0),
    new THREE.Vector3(-1.05, syringeY + 0.15, 0),
    new THREE.Vector3(-0.55, syringeY, 0),
  ]);
  const tubeMaterial = labGlassMaterial(stage, 0.4);
  tubeMaterial.side = THREE.DoubleSide;
  const tube = new THREE.Mesh(keep(new THREE.TubeGeometry(curve, 80, 0.055, 12, false)), tubeMaterial);
  tube.renderOrder = 12;
  scene.add(tube);
  syringe.setGasOpacity(0.07);
  stage.finish();

  const matrix = new THREE.Matrix4();
  const point = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const euler = new THREE.Euler();
  const scale = new THREE.Vector3();
  const hidden = new THREE.Matrix4().makeScale(0, 0, 0);

  return {
    update: (state, t, playing) => {
      if (Math.abs(state.acidVolumeMl - lastAcidMl) > 0.01) {
        lastAcidMl = state.acidVolumeMl;
        acidHeight = liquidHeight(THREE.MathUtils.clamp(state.acidVolumeMl / FLASK_CAPACITY_ML, 0.03, 1));
        const next = new THREE.LatheGeometry(liquidProfile(acidHeight), 72);
        acidGeometry.dispose();
        acidGeometry = next;
        acid.geometry = next;
      }

      const layout = CHIP_COUNTS[state.surfaceArea];
      const shrink = 1 - 0.3 * THREE.MathUtils.clamp(state.progress, 0, 1);
      for (let index = 0; index < MAX_CHIPS; index += 1) {
        if (index >= layout.count) {
          marble.setMatrixAt(index, hidden);
          continue;
        }
        const spot = chipSpots[index];
        const size = layout.size * spot.scale * shrink;
        const r = spot.radius * layout.spread;
        point.set(flaskX + Math.cos(spot.angle) * r, floorY + 0.05 + size * 0.8, Math.sin(spot.angle) * r);
        euler.set(spot.tilt, spot.spin, spot.tilt * 0.5);
        marble.setMatrixAt(index, matrix.compose(point, quaternion.setFromEuler(euler), scale.setScalar(size)));
      }
      marble.instanceMatrix.needsUpdate = true;

      const active = state.bubbleRate > 0.001 ? Math.max(3, Math.round(THREE.MathUtils.clamp(state.bubbleRate, 0, 1) * BUBBLES)) : 0;
      const rise = Math.max(acidHeight - 0.12, 0.05);
      for (let index = 0; index < BUBBLES; index += 1) {
        if (index >= active) {
          bubbles.setMatrixAt(index, hidden);
          continue;
        }
        const spot = chipSpots[index % layout.count];
        const progress = (t * (0.35 + seeded(index, 31) * 0.5) + seeded(index, 32)) % 1;
        const y = floorY + 0.1 + progress * rise;
        const maxR = flaskInnerRadius(y - floorY) - 0.08;
        const r = Math.min(spot.radius * layout.spread + (seeded(index, 33) - 0.5) * 0.25, maxR);
        point.set(flaskX + Math.cos(spot.angle + index) * Math.max(r, 0) + Math.sin(t * 5 + index) * 0.02, y, Math.sin(spot.angle + index) * Math.max(r, 0));
        bubbles.setMatrixAt(index, matrix.compose(point, quaternion.identity(), scale.setScalar(0.6 + progress)));
      }
      bubbles.instanceMatrix.needsUpdate = true;

      syringe.setFill(state.capacityMl > 0 ? state.gasMl / state.capacityMl : 0);
      stage.render(playing);
    },
    resize: fitToWidth(stage, 3.9),
    dispose: () => {
      acidGeometry.dispose();
      stage.dispose();
    },
  };
};
